import type { RiskAdjustedReturns, RiskFactor } from '../../types/calculations';
import { formatPercent } from '../../utils/formatters';

interface RiskBandVisualizationProps {
  riskReturns: RiskAdjustedReturns;
}

interface RiskBandRowProps {
  label: string;
  downside: number | null | undefined;
  base: number | null | undefined;
  upside: number | null | undefined;
}

function getConfidenceColor(confidence: string): { text: string; bg: string; border: string } {
  const level = confidence.toLowerCase();
  if (level === 'high') return { text: 'text-emerald', bg: 'bg-emerald/10', border: 'border-emerald/30' };
  if (level === 'medium') return { text: 'text-gold', bg: 'bg-gold/10', border: 'border-gold/30' };
  return { text: 'text-crimson', bg: 'bg-crimson/10', border: 'border-crimson/30' };
}

function getRiskLevelColor(level: 'low' | 'medium' | 'high'): { text: string; bg: string } {
  if (level === 'low') return { text: 'text-emerald', bg: 'bg-emerald/10' };
  if (level === 'medium') return { text: 'text-gold', bg: 'bg-gold/10' };
  return { text: 'text-crimson', bg: 'bg-crimson/10' };
}

// Plain-English labels and summaries for risk factors
const RISK_FACTOR_LABELS: Record<string, { label: string; summary: (level: string, value: number) => string }> = {
  employment_concentration: {
    label: 'Job Market Diversity',
    summary: (level) => {
      if (level === 'low') return 'Diversified local economy across multiple industries — reduces tenant risk';
      if (level === 'medium') return 'Moderate industry diversity — no major concentration risk';
      return 'Jobs concentrated in few industries — an economic shift could impact tenants';
    },
  },
  supply_pipeline: {
    label: 'New Construction',
    summary: (level, value) => {
      if (level === 'low') return `Only ${Math.round(value)} permits nearby — limited new competition`;
      if (level === 'medium') return `${Math.round(value)} permits nearby — moderate new supply expected`;
      return `${Math.round(value)} permits nearby — significant new supply could pressure rents`;
    },
  },
  vacancy_trend: {
    label: 'Vacancy Trend',
    summary: (level) => {
      if (level === 'low') return 'Tight rental market with strong occupancy — renters have fewer options';
      if (level === 'medium') return 'Balanced rental market with healthy occupancy levels';
      return 'Elevated vacancies suggest softening demand — may take longer to fill units';
    },
  },
  demographic_trajectory: {
    label: 'Neighborhood Direction',
    summary: (level) => {
      if (level === 'low') return 'Neighborhood is improving — rising incomes and growing population';
      if (level === 'medium') return 'Stable neighborhood with consistent demographic trends';
      return 'Neighborhood may be softening — watch for declining population or incomes';
    },
  },
};

/**
 * Build a plain-English summary of the risk-adjusted return range.
 */
function buildRiskSummary(riskReturns: RiskAdjustedReturns): string | null {
  const baseIrr = riskReturns.base_irr_5yr;
  const downIrr = riskReturns.downside_irr_5yr;
  const upIrr = riskReturns.upside_irr_5yr;

  if (baseIrr != null && downIrr != null && upIrr != null) {
    return `Your ${formatPercent(baseIrr)} base IRR could range from ${formatPercent(downIrr)} to ${formatPercent(upIrr)} depending on market conditions`;
  }

  const baseCoc = riskReturns.base_coc_avg;
  const downCoc = riskReturns.downside_coc_avg;
  const upCoc = riskReturns.upside_coc_avg;
  if (baseCoc != null && downCoc != null && upCoc != null) {
    return `Your ${formatPercent(baseCoc)} base cash-on-cash could range from ${formatPercent(downCoc)} to ${formatPercent(upCoc)} depending on market risk`;
  }

  return null;
}

function RiskBandRow({ label, downside, base, upside }: RiskBandRowProps) {
  // Skip row if both base and upside are null (metric not available)
  if ((base === null || base === undefined) && (upside === null || upside === undefined)) {
    return null;
  }

  // Calculate range for visual positioning (use 0 as floor)
  const min = Math.min(downside ?? 0, base ?? 0, upside ?? 0, 0);
  const max = Math.max(downside ?? 0, base ?? 0, upside ?? 0);
  const range = max - min;

  // Calculate positions as percentages (0-100)
  const downsidePos = range > 0 ? ((downside ?? min) - min) / range * 100 : 0;
  const basePos = range > 0 ? ((base ?? min) - min) / range * 100 : 50;
  const upsidePos = range > 0 ? ((upside ?? max) - min) / range * 100 : 100;

  return (
    <div className="py-3 border-b border-slate-dark last:border-b-0">
      {/* Label Row */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-ivory">{label}</span>
        <div className="flex items-center gap-3 text-sm font-mono text-stone-700">
          <span title="Downside">{formatPercent(downside ?? 0)}</span>
          <span title="Base" className="font-bold text-gold">{formatPercent(base ?? 0)}</span>
          <span title="Upside">{formatPercent(upside ?? 0)}</span>
        </div>
      </div>

      {/* Range Bar Visualization */}
      <div className="relative h-6 bg-graphite rounded">
        {/* Downside to Base segment (crimson tint) */}
        <div
          className="absolute h-full bg-crimson/20 rounded-l transition-all"
          style={{
            left: `${downsidePos}%`,
            width: `${basePos - downsidePos}%`,
          }}
        />

        {/* Base to Upside segment (emerald tint) */}
        <div
          className="absolute h-full bg-emerald/20 rounded-r transition-all"
          style={{
            left: `${basePos}%`,
            width: `${upsidePos - basePos}%`,
          }}
        />

        {/* Base marker (gold line) */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-gold shadow-md z-10"
          style={{ left: `${basePos}%` }}
        />

        {/* Downside marker */}
        <div
          className="absolute top-0 bottom-0 w-px bg-crimson/50"
          style={{ left: `${downsidePos}%` }}
        />

        {/* Upside marker */}
        <div
          className="absolute top-0 bottom-0 w-px bg-emerald/50"
          style={{ left: `${upsidePos}%` }}
        />
      </div>
    </div>
  );
}

function RiskFactorDetail({ factor }: { factor: RiskFactor }) {
  const colors = getRiskLevelColor(factor.risk_level);
  const meta = RISK_FACTOR_LABELS[factor.factor_name];

  return (
    <div className="py-3 border-b border-slate-dark last:border-b-0">
      {/* Factor Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-ivory">
              {meta?.label || factor.factor_name.replace(/_/g, ' ')}
            </span>
            <span className={`px-2 py-0.5 rounded text-sm font-semibold uppercase ${colors.bg} ${colors.text}`}>
              {factor.risk_level}
            </span>
            <span className="text-sm text-stone-700">
              ({(factor.weight * 100).toFixed(0)}% weight)
            </span>
          </div>
          {/* Plain-English Explanation */}
          <p className="text-sm text-ivory/60 leading-relaxed">
            {meta?.summary(factor.risk_level, factor.current_value) || factor.narrative}
          </p>
        </div>
      </div>

      {/* Simplified Impact */}
      <div className="flex items-center gap-4 text-sm font-mono text-stone-700 mt-2">
        <div>
          <span className="text-stone-700">Impact:</span>{' '}
          <span className={factor.risk_level === 'low' ? 'text-emerald' : factor.risk_level === 'high' ? 'text-crimson' : 'text-gold'}>
            {factor.risk_level === 'low' ? 'Tightens' : factor.risk_level === 'high' ? 'Widens' : 'Neutral'}
          </span>
          {' '}range by {formatPercent(Math.abs(factor.weighted_adjustment_pct))}
        </div>
      </div>
    </div>
  );
}

export function RiskBandVisualization({ riskReturns }: RiskBandVisualizationProps) {
  const { confidence_level = 'medium', factors_available = 0, factors_total = 4 } = riskReturns as any;
  const confidenceColors = getConfidenceColor(confidence_level);

  return (
    <div className="space-y-6">
      {/* Header with Confidence Badge */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg uppercase tracking-[0.12em] text-gold font-semibold">
          Risk-Adjusted Returns
        </h3>

        {/* Always-visible confidence badge */}
        <div className={`px-3 py-1 rounded border ${confidenceColors.bg} ${confidenceColors.text} ${confidenceColors.border}`}>
          <span className="text-sm font-semibold uppercase">
            {confidence_level} Confidence
          </span>
          <span className="text-sm ml-2 opacity-75">
            ({factors_available}/{factors_total} factors)
          </span>
        </div>
      </div>

      {/* Plain-English Summary */}
      {(() => {
        const summary = buildRiskSummary(riskReturns);
        return summary ? (
          <p className="text-sm text-ivory/70 leading-relaxed -mt-2">
            {summary}
          </p>
        ) : null;
      })()}

      {/* Range Bar Chart */}
      <div className="rounded-lg border border-slate-dark bg-charcoal p-4">
        <RiskBandRow
          label="5yr IRR"
          downside={riskReturns.downside_irr_5yr}
          base={riskReturns.base_irr_5yr}
          upside={riskReturns.upside_irr_5yr}
        />
        <RiskBandRow
          label="10yr IRR"
          downside={riskReturns.downside_irr_10yr}
          base={riskReturns.base_irr_10yr}
          upside={riskReturns.upside_irr_10yr}
        />
        <RiskBandRow
          label="Avg CoC"
          downside={riskReturns.downside_coc_avg}
          base={riskReturns.base_coc_avg}
          upside={riskReturns.upside_coc_avg}
        />
      </div>

      {/* Risk Factor Detail */}
      <div className="rounded-lg border border-slate-dark bg-charcoal p-4">
        <h4 className="text-sm uppercase tracking-wide text-gold mb-3">Risk Factors</h4>

        {riskReturns.risk_factors.length === 0 ? (
          <p className="text-sm text-stone-700 italic">No risk factors available</p>
        ) : (
          <div>
            {riskReturns.risk_factors.map((factor, idx) => (
              <RiskFactorDetail key={idx} factor={factor} />
            ))}
          </div>
        )}
      </div>

      {/* Band Width Info */}
      <div className="text-sm text-stone-700 px-2">
        <span className="font-semibold">Band width:</span> ±{formatPercent(riskReturns.band_width_pct)}
      </div>
    </div>
  );
}
